"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getPrintCartItemKey, isPrintCart, PRINT_CART_STORAGE_KEY, PRINT_CART_STORAGE_VERSION, type PrintCartItem } from "@/lib/print-cart-contract";
type AddItemResult = {
  ok: true;
} | {
  ok: false;
  error: string;
};
type PrintCartContextValue = {
  items: PrintCartItem[];
  hydrated: boolean;
  itemCount: number;
  addItem: (item: PrintCartItem) => AddItemResult;
  removeItem: (item: PrintCartItem) => void;
  clear: () => void;
};
const PrintCartContext = createContext<PrintCartContextValue | null>(null);
export function PrintCartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<PrintCartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PRINT_CART_STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      if (isPrintCart(parsed)) setItems(parsed.items);
    } catch {
      // A malformed or unavailable localStorage entry should never block checkout.
    } finally {
      setHydrated(true);
    }
  }, []);
  useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(PRINT_CART_STORAGE_KEY, JSON.stringify({
        version: PRINT_CART_STORAGE_VERSION,
        items
      }));
    } catch {
      // Quota/privacy mode failures leave the in-memory cart usable.
    }
  }, [hydrated, items]);
  const addItem = useCallback((item: PrintCartItem): AddItemResult => {
    if (!Number.isInteger(item.design.eventId) || item.design.eventId <= 0) {
      return {
        ok: false,
        error: "Dette kort kan ikke lægges i kurven."
      };
    }
    const eventId = itemsRef.current[0]?.design.eventId;
    if (eventId && eventId !== item.design.eventId) {
      return {
        ok: false,
        error: "Kurven kan kun indeholde kort til ét event ad gangen."
      };
    }
    setItems(current => {
      const existingIndex = current.findIndex(entry => entry.type === item.type);
      if (existingIndex === -1) return [...current, item];
      return current.map((entry_0, index) => index === existingIndex ? item : entry_0);
    });
    return {
      ok: true
    };
  }, []);
  const removeItem = useCallback((item_0: PrintCartItem) => {
    const key = getPrintCartItemKey(item_0);
    setItems(current_0 => current_0.filter(entry_1 => getPrintCartItemKey(entry_1) !== key));
  }, []);
  const clear = useCallback(() => setItems([]), []);
  const value = useMemo(() => ({
    items,
    hydrated,
    itemCount: items.length,
    addItem,
    removeItem,
    clear
  }), [addItem, clear, hydrated, items, removeItem]);
  return <PrintCartContext.Provider value={value}>{children}</PrintCartContext.Provider>;
}
export function usePrintCart() {
  const context = useContext(PrintCartContext);
  if (!context) {
    throw new Error("usePrintCart must be used within PrintCartProvider");
  }
  return context;
}